'use client'

import { useState } from 'react'
import { Mail, CheckCircle } from 'lucide-react'
import { Button } from '@/components/ui/button'

const INTEREST_OPTIONS = [
  { id: 'general', label: 'Everything (For You)' },
  { id: 'business', label: 'Business & Productivity' },
  { id: 'creative', label: 'Creative & Content' },
  { id: 'education', label: 'Education & Learning' },
  { id: 'technical', label: 'Technical & Tools' },
  { id: 'innovation', label: 'News & Innovation' },
]

export default function NewsletterPage() {
  const [email, setEmail] = useState('')
  const [interests, setInterests] = useState<string[]>(['general'])
  const [loading, setLoading] = useState(false)
  const [success, setSuccess] = useState(false)
  const [error, setError] = useState('')

  function toggleInterest(id: string) {
    setInterests((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
    )
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setLoading(true)
    setError('')

    try {
      const res = await fetch('/api/newsletter/subscribe', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, interests }),
      })

      if (!res.ok) {
        const data = await res.json()
        throw new Error(data.error || 'Something went wrong. Please try again.')
      }

      setSuccess(true)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="bg-background text-charcoal">

      <section className="py-20 sm:py-28">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <div className="flex justify-center mb-6">
              <Mail className="w-16 h-16 text-primary" />
            </div>
            <h1 className="text-4xl sm:text-5xl font-bold mb-4">
              Stay in the loop
            </h1>
            <p className="text-xl text-warm-gray">
              3 AI things that matter, every Monday.
            </p>
          </div>

          {/* Newsletter Signup Form */}
          <div className="bg-white rounded-lg border border-gray-200 p-8 sm:p-12 mb-12">
            {success ? (
              <div className="text-center py-8">
                <CheckCircle className="w-16 h-16 text-primary mx-auto mb-4" />
                <h2 className="text-2xl font-bold text-charcoal mb-2">You&apos;re in!</h2>
                <p className="text-warm-gray">
                  Check your inbox Monday morning for your first issue.
                </p>
              </div>
            ) : (
              <>
                <h2 className="text-2xl font-bold mb-6 text-charcoal">Subscribe now</h2>

                <form className="space-y-4" onSubmit={handleSubmit}>
                  <div>
                    <label className="block text-sm font-semibold text-charcoal mb-2">
                      Email address
                    </label>
                    <input
                      type="email"
                      placeholder="your@email.com"
                      className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-primary focus:outline-none transition-colors"
                      required
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-charcoal mb-3">
                      What interests you most?
                    </label>
                    <div className="space-y-3">
                      {INTEREST_OPTIONS.map((option) => (
                        <label key={option.id} className="flex items-center gap-3 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={interests.includes(option.id)}
                            onChange={() => toggleInterest(option.id)}
                            className="w-4 h-4 rounded border-gray-300 text-primary focus:ring-primary"
                          />
                          <span className="text-warm-gray">{option.label}</span>
                        </label>
                      ))}
                    </div>
                  </div>

                  {error && (
                    <p className="text-sm text-red-600">{error}</p>
                  )}

                  <div className="pt-2">
                    <Button
                      type="submit"
                      disabled={loading}
                      className="w-full bg-primary hover:bg-primary/90 text-white py-3 text-base"
                    >
                      {loading ? 'Subscribing…' : 'Subscribe to the newsletter'}
                    </Button>
                  </div>

                  <p className="text-xs text-warm-gray text-center">
                    We send one email every Monday. No spam, ever. Unsubscribe anytime.
                  </p>
                </form>
              </>
            )}
          </div>

          {/* What to Expect */}
          <div className="bg-primary/5 rounded-lg border border-primary/10 p-8">
            <h3 className="text-xl font-bold text-charcoal mb-4">What you&apos;ll get</h3>
            <ul className="space-y-3">
              {[
                'One practical tip you can use right now',
                'One newsworthy development in AI that matters',
                'One tool or resource worth trying',
              ].map((item, i) => (
                <li key={i} className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-primary mt-1 flex-shrink-0" />
                  <span className="text-charcoal">{item}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Sample Newsletter */}
          <div className="mt-16">
            <h3 className="text-2xl font-bold text-charcoal mb-8">Sample from last week</h3>

            <div className="bg-white rounded-lg border border-gray-200 p-8">
              <div className="text-sm text-warm-gray mb-6">
                Monday, February 24, 2025
              </div>

              <div className="space-y-8">
                <div>
                  <h4 className="text-lg font-bold text-charcoal mb-2">
                    💡 Tip: Prompts are about clarity, not length
                  </h4>
                  <p className="text-warm-gray leading-relaxed">
                    Most people write long, rambling prompts thinking AI needs more detail. Actually, the opposite: short, specific prompts work better. &quot;Write a product description for a coffee maker under 100 words&quot; beats &quot;Write a product description for our coffee maker that&apos;s good and talks about how cool it is and the features.&quot; Clarity beats verbosity.
                  </p>
                </div>

                <div>
                  <h4 className="text-lg font-bold text-charcoal mb-2">
                    🚀 News: Claude 3.5 is faster and cheaper
                  </h4>
                  <p className="text-warm-gray leading-relaxed">
                    Anthropic just announced Claude 3.5 improvements. Processing cost dropped 20%, response time is 30% faster. If you&apos;re using Claude at scale (or thinking about it), now&apos;s a good time to test it. We compared it to ChatGPT 4 and found it better for reasoning tasks.
                  </p>
                </div>

                <div>
                  <h4 className="text-lg font-bold text-charcoal mb-2">
                    🛠️ Tool: Cursor (AI IDE)
                  </h4>
                  <p className="text-warm-gray leading-relaxed">
                    Not just for programmers. Cursor is a code editor that uses AI to help you write code. If you do any coding—or want to learn—try it. Free version is fully functional. Takes about 10 minutes to feel useful. We tested it and it&apos;s legitimately impressive.
                  </p>
                </div>
              </div>

              <div className="border-t border-gray-200 pt-6 mt-8">
                <p className="text-sm text-warm-gray">
                  That&apos;s it. Three things, no fluff, every Monday.
                </p>
              </div>
            </div>
          </div>

          {/* Why Subscribe */}
          <div className="mt-16 grid sm:grid-cols-3 gap-6">
            {[
              { icon: '⏱️', title: 'Takes 3 minutes', description: 'Just three useful things, not a 20-minute read' },
              { icon: '✨', title: 'Carefully curated', description: 'We find the signal, filter out the noise' },
              { icon: '🎯', title: 'Actually useful', description: 'Not hype. Real insights you can apply today' },
            ].map((item, i) => (
              <div key={i} className="text-center">
                <div className="text-4xl mb-3">{item.icon}</div>
                <h4 className="font-semibold text-charcoal mb-2">{item.title}</h4>
                <p className="text-sm text-warm-gray">{item.description}</p>
              </div>
            ))}
          </div>

          {/* CTA */}
          <div className="mt-16 text-center bg-primary/5 rounded-lg border border-primary/10 p-8">
            <h3 className="text-2xl font-bold text-charcoal mb-4">
              Ready to stay in the loop?
            </h3>
            <p className="text-warm-gray mb-6">
              Get the next issue delivered to your inbox Monday morning.
            </p>
            <Button
              onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
              className="bg-primary hover:bg-primary/90 text-white px-8 py-3"
            >
              Subscribe now
            </Button>
          </div>
        </div>
      </section>

    </div>
  )
}
